import {
  ArrowLeftStartOnRectangleIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ClockIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  FlagIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { Link, NavLink, useLocation } from 'react-router-dom';

import { ImpersonationBanner } from '@/components/ImpersonationBanner';
import { Avatar } from '@/components/ui/Avatar';
import { DropdownItem, DropdownMenu, DropdownSeparator } from '@/components/ui/DropdownMenu';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/cn';

interface AppShellProps {
  children: React.ReactNode;
}

interface NavEntry {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const ADMIN_NAV: NavEntry[] = [
  { to: '/admin/overview', label: 'Overview', icon: ChartBarIcon },
  { to: '/admin/users', label: 'Users', icon: UserGroupIcon },
  { to: '/admin/orgs', label: 'Organizations', icon: BuildingOffice2Icon },
  { to: '/admin/tiers', label: 'Tiers', icon: CreditCardIcon },
  { to: '/admin/feedback', label: 'Feedback', icon: ChatBubbleLeftRightIcon },
  { to: '/admin/audit', label: 'Audit log', icon: ClockIcon },
];

interface NavSectionProps {
  label: string;
  entries: NavEntry[];
}

function NavSection(props: NavSectionProps) {
  const { label, entries } = props;
  return (
    <div className="px-2 py-1.5">
      <p className="px-2 pb-1.5 pt-3 text-[10px] font-semibold uppercase tracking-wider text-brand-400">
        {label}
      </p>
      <ul className="flex flex-col gap-0.5">
        {entries.map((entry) => {
          const Icon = entry.icon;
          return (
            <li key={entry.to}>
              <NavLink
                to={entry.to}
                end
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors',
                    isActive
                      ? 'bg-brand-900 text-white'
                      : 'text-brand-700 hover:bg-brand-100',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <Icon className={cn('h-4 w-4', isActive ? 'text-white' : 'text-brand-400')} />
                    <span>{entry.label}</span>
                  </>
                )}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function AppShell(props: AppShellProps) {
  const { children } = props;
  const { me, signOut } = useAuth();
  const location = useLocation();
  const isSuperAdmin = me?.user.role === 'super_admin';
  const memberships = me?.memberships ?? [];

  return (
    <div className="flex min-h-screen flex-col bg-brand-50">
      <ImpersonationBanner />
      <header className="flex items-center justify-between border-b border-brand-200 bg-white px-6 py-2.5">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-brand-900 text-sm font-bold text-white">
            K
          </div>
          <span className="text-sm font-semibold text-brand-900">Kiro</span>
        </Link>

        <DropdownMenu
          trigger={
            <button className="flex items-center gap-2 rounded-md px-2 py-1 text-sm text-brand-700 hover:bg-brand-100">
              <Avatar name={me?.user.email ?? '?'} />
              <span className="hidden sm:inline">{me?.user.email}</span>
              <ChevronDownIcon className="h-4 w-4 text-brand-400" />
            </button>
          }
        >
          <div className="px-3 py-2 text-xs text-brand-500">
            {isSuperAdmin ? 'Super-admin' : 'Member'}
          </div>
          <DropdownSeparator />
          <DropdownItem icon={<UserIcon className="h-4 w-4" />} disabled>
            {me?.user.email}
          </DropdownItem>
          <DropdownItem
            icon={<ArrowLeftStartOnRectangleIcon className="h-4 w-4" />}
            onClick={signOut}
          >
            Sign out
          </DropdownItem>
        </DropdownMenu>
      </header>

      <div className="flex flex-1 min-h-0">
        <nav className="hidden w-56 shrink-0 overflow-y-auto border-r border-brand-200 bg-white py-2 md:block">
          <NavSection
            label="My account"
            entries={[
              { to: '/me', label: 'Dashboard', icon: UserIcon },
              { to: '/me/follow-ups', label: 'Follow-ups', icon: FlagIcon },
            ]}
          />

          {memberships.map((m) => {
            const base = `/org/${m.organizationSlug}`;
            const entries: NavEntry[] = [
              { to: `${base}/overview`, label: 'Overview', icon: ChartBarIcon },
            ];
            if (m.role === 'admin') {
              entries.push({ to: `${base}/users`, label: 'Members', icon: UserGroupIcon });
              entries.push({ to: `${base}/settings`, label: 'Settings', icon: Cog6ToothIcon });
            }
            return <NavSection key={m.organizationId} label={m.organizationName} entries={entries} />;
          })}

          {isSuperAdmin && <NavSection label="Super-admin" entries={ADMIN_NAV} />}
        </nav>

        <main key={location.pathname} className="flex-1 overflow-y-auto px-4 py-6 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">{children}</div>
        </main>
      </div>
    </div>
  );
}
